import React, { useState } from "react";

import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";

/**
 * A labelled status cell: the field name with its explanation behind an info
 * icon, and the value as a chip beneath (FG-8053).
 *
 * Every one of these fields is a coded value whose meaning is not obvious, and
 * the ones derived by Periscope routinely disagree with the ones that come from
 * billing — so each cell says what it is and what the current value means.
 */
/** A bare status pill's width, and the gap between the pair on a row. */
const NARROW_CELL = 90;
const STATUS_GAP = 24;

export interface StatusValueHelp {
  label: string;
  /** Chip styling from mainStyles, so the legend cannot drift from the value. */
  style: object;
  description: string;
}

export interface StatusCellProps {
  label: string;
  /** What the field is. */
  help: string;
  /** Every value the field can take, with what each one means. */
  values?: StatusValueHelp[];
  /** Footnote for the caveat that does not belong on any single value. */
  note?: { text: string; linkHref?: string; linkLabel?: string };
  value: React.ReactNode;
  chipStyle?: object;
  /** Shown under the chip: an identifier or note tied to this status. */
  secondary?: React.ReactNode;
  /** Two thirds of the row rather than one, for a status carrying an id. */
  wide?: boolean;
  testId?: string;
}

/**
 * Hovering explains the field and what its current value means; "Find out more"
 * opens the full list of values. Most of the time the question is "what does
 * this value mean?", which the hover answers without leaving the page - the
 * dialog is there so nothing has to be truncated to fit a tooltip.
 */
export const StatusCell = ({
  label,
  help,
  values,
  note,
  value,
  chipStyle,
  secondary,
  wide,
  testId,
}: StatusCellProps) => {
  const [open, setOpen] = useState(false);
  const currentLabel = typeof value === "string" ? value : undefined;
  const current = values?.find((entry) => entry.label === currentLabel);

  const chip = (
    <Chip
      size="small"
      label={currentLabel}
      sx={{
        fontWeight: 700,
        height: "auto",
        py: 0.25,
        "& .MuiChip-label": { whiteSpace: "normal", lineHeight: 1.3, px: 1 },
        ...chipStyle,
      }}
    />
  );

  const tooltipBody = (
    <Box sx={{ maxWidth: 280 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5 }}>{help}</Typography>
      {current && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: 1,
            borderColor: "rgba(255,255,255,0.24)",
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              opacity: 0.7,
              mb: 0.5,
            }}
          >
            This org
          </Typography>
          <Box sx={{ mb: 0.5 }}>{chip}</Box>
          <Typography sx={{ fontSize: 12.5 }}>{current.description}</Typography>
        </Box>
      )}
      {values && values.length > 1 && (
        <Link
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{
            mt: 1,
            fontSize: 12,
            fontWeight: 700,
            color: "#B3C2FF",
            textDecorationColor: "#B3C2FF",
            textAlign: "left",
          }}
        >
          Find out more…
        </Link>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.5,
        minWidth: 0,
        // A bare pill needs only its own width, and a fixed one keeps the two
        // rows aligned; the status carrying an identifier takes everything left
        // over, which is what keeps the id beside its pill. The two bases add
        // up to the row exactly, so it is always one pair per row - a basis of
        // zero let all four crowd onto one line.
        flexGrow: wide ? 1 : 0,
        flexShrink: wide ? 1 : 0,
        flexBasis: wide
          ? `calc(100% - ${NARROW_CELL + STATUS_GAP}px)`
          : NARROW_CELL,
      }}
      data-testid={testId}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            color: "text.disabled",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        {/* An IconButton inside the Tooltip, as the detail panes do it: a bare
            icon only opens on hover, this also opens on click, focus and touch.
            The tooltip stays interactive so its link is reachable. */}
        <Tooltip
          title={tooltipBody}
          placement="top"
          arrow
          enterTouchDelay={0}
          leaveDelay={200}
          leaveTouchDelay={8000}
        >
          <IconButton
            size="small"
            aria-label={`About ${label}`}
            onClick={() => values && values.length > 1 && setOpen(true)}
            sx={{ p: 0.125, color: "text.disabled" }}
          >
            <InfoIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {/* The identifier sits beside the pill rather than under it: the pill is
          narrow, and the row it would otherwise take is the whole reason this
          card was tall. */}
      {secondary ? (
        // No wrapping: the identifier measures itself against the space it is
        // given, so it shortens to stay beside the pill rather than dropping
        // onto a line of its own.
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
            minWidth: 0,
            // The cell is a column with align-items: flex-start, so its children
            // are content-sized unless they say otherwise. Without this the id
            // measures itself against its own text and shrinks to nothing.
            alignSelf: "stretch",
            "& > *:first-of-type": { flex: "none" },
          }}
        >
          {typeof value === "string" ? chip : value}
          {/* The identifier gets the rest of the row as a definite width: it
              measures itself against what it is given, and a content-sized
              parent would leave it measuring its own text. */}
          <Box sx={{ minWidth: 0, flex: "1 1 0" }}>{secondary}</Box>
        </Box>
      ) : (
        <>{typeof value === "string" ? chip : value}</>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>{label}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            {help}
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "text.disabled",
              mb: 1,
            }}
          >
            Possible values
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {(values ?? []).map((entry) => (
              <Box
                key={entry.label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "minmax(0, 215px) 1fr",
                  },
                  gap: { xs: 0.5, sm: 2 },
                  alignItems: "start",
                }}
              >
                <Chip
                  size="small"
                  label={entry.label}
                  sx={{
                    fontWeight: 700,
                    height: "auto",
                    py: 0.25,
                    justifySelf: "start",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      lineHeight: 1.3,
                      px: 1,
                    },
                    ...entry.style,
                  }}
                />
                <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                  {entry.description}
                </Typography>
              </Box>
            ))}
          </Box>
          {note && (
            <Box
              sx={{
                mt: 2,
                p: 1.25,
                borderLeft: 3,
                borderColor: "primary.main",
                bgcolor: "action.hover",
                borderRadius: "0 4px 4px 0",
              }}
            >
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {note.text}
                {note.linkHref && (
                  <>
                    {" "}
                    <Link
                      href={note.linkHref}
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                    >
                      {note.linkLabel ?? "More info"}
                    </Link>
                  </>
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** A meter for "used of purchased", so an over- or under-provisioned org reads at a glance. */
export interface SeatMeterProps {
  label: string;
  used: number;
  purchased: number;
}

export const SeatMeter = ({ label, used, purchased }: SeatMeterProps) => {
  // Occupying more seats than were bought is a billing problem, not a full
  // bar: the meter cannot show it by length, so it says so in colour and
  // spells out the overage.
  const isOverAllocated = used > purchased;
  const filled =
    purchased > 0
      ? Math.min(100, (used / purchased) * 100)
      : used > 0
        ? 100
        : 0;
  const overBy = used - purchased;

  return (
    <Tooltip
      title={
        isOverAllocated
          ? `${used.toLocaleString()} ${label.toLowerCase()} seats occupied against ${purchased.toLocaleString()} purchased - ${overBy.toLocaleString()} over.`
          : ""
      }
    >
      <Box
        sx={{
          display: "grid",
          // Each meter is its own grid, so an auto-sized count column would be
          // a different width per row - "0 / 100" is wider than "0 / 0" - and
          // the 1fr bar would absorb the difference, leaving three bars of
          // three lengths. A floor on the column keeps them aligned, and it
          // still grows for counts wider than any of them.
          gridTemplateColumns: "46px 1fr minmax(64px, auto)",
          gap: 1.25,
          alignItems: "center",
          fontSize: 12.5,
        }}
        data-testid={`dashboard-seat-meter-${label.toLowerCase()}`}
        data-over-allocated={isOverAllocated ? "true" : undefined}
      >
        <Typography
          sx={{ fontSize: 12.5, fontWeight: 700, color: "text.secondary" }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: "action.selected",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${Math.max(filled, purchased > 0 && used > 0 ? 1 : 0)}%`,
              bgcolor: isOverAllocated ? "error.main" : "primary.main",
              borderRadius: 3,
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: 12.5,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            textAlign: "right",
          }}
        >
          <Box
            component="span"
            sx={{
              color: isOverAllocated ? "error.main" : "text.primary",
              fontWeight: 700,
            }}
          >
            {used.toLocaleString()}
          </Box>
          {` / ${purchased.toLocaleString()}`}
        </Typography>
      </Box>
    </Tooltip>
  );
};
